import { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * ShareStudioShell — entity-agnostic responsive layout for Share Studio.
 *
 * Mobile (< lg):  single-column vertical flow (guided).
 * Desktop (≥ lg): two-column workspace — controls left, preview right.
 *
 * Designed to be reused across entity types (activities, products, etc.).
 * The shell owns layout only — all content is injected via slots.
 *
 * Usage:
 *   <ShareStudioShell
 *     controls={<QuickShareCard />}
 *     preview={<PreviewImage />}
 *     mobilePreviewPosition="top"
 *   />
 */

interface ShareStudioShellProps {
  /** Left column content: controls, actions, text editor */
  controls: ReactNode;
  /** Right column content: live preview image/card */
  preview: ReactNode;
  /**
   * On mobile, where to render the preview relative to controls.
   * - "top": preview above controls (default, used in quick mode)
   * - "inline": preview is embedded inside controls (used in customize mode)
   * - "hidden": preview is not shown on mobile (controls handle it)
   */
  mobilePreviewPosition?: 'top' | 'inline' | 'hidden';
  /** Additional className on the root grid */
  className?: string;
}

export function ShareStudioShell({
  controls,
  preview,
  mobilePreviewPosition = 'top',
  className,
}: ShareStudioShellProps) {
  return (
    <div
      className={cn(
        'pb-4',
        // Desktop: two-column workspace
        'lg:grid lg:grid-cols-[1fr_380px] lg:gap-8 lg:items-start',
        className,
      )}
    >
      {/* Controls — left on desktop */}
      <div
        className={cn(
          'space-y-4 min-w-0',
          // On mobile with top preview, controls come second
          mobilePreviewPosition === 'top' && 'order-2 lg:order-1',
        )}
      >
        {controls}
      </div>

      {/* Preview — right on desktop */}
      {mobilePreviewPosition !== 'hidden' && (
        <div
          className={cn(
            'lg:sticky lg:top-0',
            mobilePreviewPosition === 'top' && 'order-1 lg:order-2 mb-4 lg:mb-0',
            mobilePreviewPosition === 'inline' && 'hidden lg:block',
          )}
        >
          {preview}
        </div>
      )}
    </div>
  );
}
